import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { Button } from '~/eds/src/components/Button/Button';
import { CopyButton } from '~/eds/src/components/Button/subcomponents/CopyButton';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import {
  SharepointErrorStatusTypes,
  SharepointStatusType,
} from '~/enums/SharepointType';
import { SPEConfiguration } from '~/redux/api/methods/spe';

import MicrosoftWordIntegrationInfo from './MicrosoftWordIntegrationInfo';

function MicrosoftWordIntegrationCard({
  integrationConfigurationStatus,
  saveConfiguration,
  handleGrantAdminConsent,
  isLoading,
}: {
  integrationConfigurationStatus: SPEConfiguration;
  saveConfiguration: (
    microsoftTenantId: string,
    sharePointSiteUrl: string,
    id?: string,
  ) => void;
  handleGrantAdminConsent: () => void;
  isLoading: boolean;
}) {
  const DEFAULT_INTEGRATION_CONFIG: SPEConfiguration = {
    tenantId: '',
    sharepointUrl: '',
    status: SharepointStatusType.NotConnected,
  };
  const ADMIN_CONSENT_URL = `https://login.microsoftonline.com/common/adminconsent?client_id=${getEnvVariable('EVISORT_SPE_CLIENT_ID')}&redirect_uri=${window.location.origin}`;

  const [integrationConfiguration, setIntegrationConfiguration] = useState(
    DEFAULT_INTEGRATION_CONFIG,
  );

  const [isManageConfigurationActive, setIsManageConfigurationActive] =
    useState(false);

  useEffect(() => {
    setIntegrationConfiguration(
      integrationConfigurationStatus ?? DEFAULT_INTEGRATION_CONFIG,
    );
  }, [integrationConfigurationStatus]);

  useEffect(() => {
    if (
      !isLoading &&
      isManageConfigurationActive &&
      integrationConfiguration?.status === SharepointStatusType.Connected
    ) {
      setIsManageConfigurationActive(false);
    }
  }, [
    isLoading,
    isManageConfigurationActive,
    integrationConfiguration?.status,
  ]);

  const intl = useIntl();

  const getErrorStatusMessage = () => {
    switch (integrationConfiguration?.status) {
      case SharepointStatusType.AuthorizedValidationError:
        return intl.formatMessage({
          id: 'MicrosoftWordIntegrationCard.statusMessage1',
          defaultMessage:
            "We couldn't validate the SharePoint Site URL or Microsoft Tenant ID. Check your configuration information and contact your admin if the issue persists.",
        });
      case SharepointStatusType.AuthenticationError:
      case SharepointStatusType.AuthorizedConfigurationRequired:
      default:
        return intl.formatMessage({
          id: 'MicrosoftWordIntegrationCard.statusMessage2',
          defaultMessage:
            'We encountered an issue with your authentication. Check your login details or contact your admin for help.',
        });
    }
  };

  const isShowInfo = () => {
    if (
      integrationConfiguration?.status !== SharepointStatusType.NotConnected
    ) {
      if (isManageConfigurationActive) {
        return false;
      }
      return true;
    }
    return false;
  };

  const handleManageConfiguration = () => {
    setIsManageConfigurationActive(true);
  };

  return (
    <Layout direction="column" spacing={4}>
      {SharepointErrorStatusTypes.includes(
        integrationConfiguration?.status as SharepointStatusType,
      ) && <StatusMessage message={getErrorStatusMessage()} status="danger" />}
      {isShowInfo() ? (
        <MicrosoftWordIntegrationInfo
          sharePointSiteUrl={integrationConfiguration?.sharepointUrl}
          tenantId={integrationConfiguration?.tenantId}
          status={integrationConfiguration?.status}
          onManageConfiguration={handleManageConfiguration}
        />
      ) : (
        <Layout direction="column" spacing={4} preset="sections">
          <Section
            heading="h4"
            title={intl.formatMessage({
              id: 'MicrosoftWordIntegrationCard.title1',
              defaultMessage: '1. Authenticate with Microsoft Word',
              description:
                'Title for first step of Microsoft Word integration card',
            })}
          >
            <Layout direction="column" spacing={4}>
              <Text variant="body" color="text.quiet">
                <FormattedMessage
                  id="MicrosoftWordIntegrationCard.description1"
                  defaultMessage="<strong>SharePoint admin access is required</strong> to enable the integration. We'll redirect you to Microsoft to authorize your account in a new window."
                  description="Description for first step of Microsoft Word integration card"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </Text>
              <Layout>
                <Button
                  text={intl.formatMessage({
                    id: 'MicrosoftWordIntegrationCard.connectButton',
                    defaultMessage: 'Connect',
                    description: 'Connect button text',
                  })}
                  iconPosition="left"
                  variant="primary"
                  isLoading={isLoading}
                  onClick={() => handleGrantAdminConsent()}
                />
              </Layout>
              <Layout direction="column" spacing={3} align="flex-start" py={2}>
                <Layout spacing={1}>
                  <Icon icon="info" />
                  <Text variant="body">
                    <FormattedMessage
                      id="MicrosoftWordIntegrationCard.infoText"
                      defaultMessage="Not a SharePoint admin? Send this link to an admin to continue."
                      description="Info text for first step of Microsoft Word integration card"
                    />
                  </Text>
                </Layout>
                <Layout
                  border="border.divider"
                  borderRadius="m"
                  bg="background.info"
                >
                  <TruncateText color="text.quiet" maxW={400} py={2} px={2}>
                    {ADMIN_CONSENT_URL}
                  </TruncateText>
                  <CopyButton
                    copyTooltip="Copy Link"
                    copyText={ADMIN_CONSENT_URL}
                  />
                </Layout>
              </Layout>
            </Layout>
          </Section>
          <Divider />
          <Section
            heading="h4"
            title={intl.formatMessage({
              id: 'MicrosoftWordIntegrationCard.title2',
              defaultMessage: '2. Integrate your environment',
              description:
                'Title for second step of Microsoft Word integration card',
            })}
          >
            <Layout direction="column" spacing={4}>
              <Text variant="body" color="text.quiet">
                <FormattedMessage
                  id="MicrosoftWordIntegrationCard.description2"
                  defaultMessage="Enter the SharePoint Site URL and Microsoft Tenant ID to integrate with your environment."
                  description="Description for second step of Microsoft Word integration card"
                />
              </Text>
              <Layout spacing={4}>
                <Layout w="50%">
                  <FormField<string, false>
                    info={intl.formatMessage({
                      id: 'MicrosoftWordIntegrationCard.sharepointSiteUrlInfo',
                      defaultMessage:
                        'e.g. https://www.yourcompany.sharepoint.com/sites/yoursite',
                      description: 'Info text for SharePoint Site URL field',
                    })}
                    label={intl.formatMessage({
                      id: 'MicrosoftWordIntegrationCard.sharepointSiteUrlLabel',
                      defaultMessage: 'SharePoint Site URL',
                      description: 'Label for SharePoint Site URL field',
                    })}
                    name="sharePointSiteUrl"
                    input={TextInput}
                    value={integrationConfiguration?.sharepointUrl}
                    onChange={(value) => {
                      setIntegrationConfiguration({
                        ...integrationConfiguration,
                        sharepointUrl: value ?? '',
                      });
                    }}
                    placeholder={intl.formatMessage({
                      id: 'MicrosoftWordIntegrationCard.sharepointSiteUrlPlaceholder',
                      defaultMessage: 'e.g. https://...',
                      description: 'Placeholder for SharePoint Site URL field',
                    })}
                  />
                </Layout>
                <Layout w="50%">
                  <FormField<string, false>
                    info={intl.formatMessage({
                      id: 'MicrosoftWordIntegrationCard.microsoftTenantIdInfo',
                      defaultMessage: 'Your Microsoft tenant ID (GUID format)',
                      description: 'Info text for Microsoft Tenant ID field',
                    })}
                    label={intl.formatMessage({
                      id: 'MicrosoftWordIntegrationCard.microsoftTenantIdLabel',
                      defaultMessage: 'Microsoft Tenant ID',
                      description: 'Label for Microsoft Tenant ID field',
                    })}
                    name="microsoftTenantId"
                    input={TextInput}
                    value={integrationConfiguration?.tenantId}
                    onChange={(value) => {
                      setIntegrationConfiguration({
                        ...integrationConfiguration,
                        tenantId: value ?? '',
                      });
                    }}
                    placeholder={intl.formatMessage({
                      id: 'MicrosoftWordIntegrationCard.microsoftTenantIdPlaceholder',
                      defaultMessage: 'e.g. 1234567',
                      description: 'Placeholder for Microsoft Tenant ID field',
                    })}
                  />
                </Layout>
              </Layout>
              <Layout>
                <Button
                  text={intl.formatMessage({
                    id: 'MicrosoftWordIntegrationCard.saveButton',
                    defaultMessage: 'Save Configuration',
                    description: 'Save configuration button text',
                  })}
                  variant="primary"
                  onClick={() => {
                    saveConfiguration(
                      integrationConfiguration?.tenantId,
                      integrationConfiguration?.sharepointUrl,
                      integrationConfiguration?.id,
                    );
                  }}
                  disabled={
                    !integrationConfiguration?.sharepointUrl ||
                    !integrationConfiguration?.tenantId
                  }
                  isLoading={isLoading}
                />
              </Layout>
            </Layout>
          </Section>
        </Layout>
      )}
    </Layout>
  );
}

export default MicrosoftWordIntegrationCard;
